import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';

import { Observable, map } from 'rxjs';
import { Store } from '@ngrx/store';

import { CustomIcon, IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { FinnairOrder } from '@fcom/dapi/api/models';
import { BookingAndAppState } from '@fcom/common/interfaces/booking';
import { orderData } from '@fcom/common-booking';
import { ConfigService } from '@fcom/core';
import { ButtonMode, ButtonTheme, IconPosition } from '@fcom/ui-components';
import { getEligibleAllianzInsuranceId } from '@fcom/common-booking/utils/order.utils';
import { FragmentService } from '@fcom/common/services';
import { CmsArticle } from '@fcom/core-api';

import { TravelInsuranceEligibilityId } from '../../../enums';

type AllianzContactLink = { label: string; href: string };

@Component({
  selector: 'fin-mmb-travel-insurance-route',
  templateUrl: './travel-insurance-route.component.html',
  styleUrls: ['./travel-insurance-route.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageBookingTravelInsuranceComponent implements OnInit {
  private store$ = inject<Store<BookingAndAppState>>(Store);
  private configService = inject(ConfigService);
  private fragmentService = inject(FragmentService);
  public readonly SvgLibraryIcon = SvgLibraryIcon;
  public readonly CustomIcon = CustomIcon;
  public readonly IconLibrary = IconLibrary;
  public readonly ButtonMode = ButtonMode;
  public readonly ButtonTheme = ButtonTheme;
  public readonly IconPosition = IconPosition;
  public readonly TravelInsuranceEligibilityId = TravelInsuranceEligibilityId;

  order$: Observable<FinnairOrder>;
  mainContentArticle$: Observable<CmsArticle>;
  pageContactInfo$: Observable<CmsArticle>;
  visibleAllianzBannerId$: Observable<TravelInsuranceEligibilityId | null>;
  showComprehensiveInsurancePanel$: Observable<boolean>;
  allianzContactInfoSectionLinks$: Observable<AllianzContactLink[]>;

  ngOnInit(): void {
    this.order$ = this.store$.pipe(orderData());
    this.visibleAllianzBannerId$ = getEligibleAllianzInsuranceId(this.order$, this.configService.cfg);

    this.showComprehensiveInsurancePanel$ = this.visibleAllianzBannerId$.pipe(
      map(
        (id) =>
          id === TravelInsuranceEligibilityId.TRAVEL_INSURANCE_24_HOUR ||
          id === TravelInsuranceEligibilityId.TRAVEL_INSURANCE_48_HOUR
      )
    );

    this.mainContentArticle$ = this.fragmentService.getFragment<CmsArticle>('MMB.fragments.allianz.pageIntro.url');

    this.pageContactInfo$ = this.fragmentService.getFragment<CmsArticle>('MMB.fragments.allianz.pageContactInfo.url');

    this.allianzContactInfoSectionLinks$ = this.pageContactInfo$.pipe(
      map((fragment) => {
        const links = fragment?.localSettings?.allianzLinks;
        if (!this.isAllianzLinks(links)) {
          return [];
        }

        return Object.entries(links).map(([label, href]) => ({ label, href }));
      })
    );
  }

  private isAllianzLinks(value: unknown): value is Record<string, string> {
    return typeof value === 'object' && value !== null && Object.values(value).every((x) => typeof x === 'string');
  }
}
